import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { alertStore, type Alert } from './alertStore';
import type { IAlertService } from './alert-service';

export interface AlertContextValue extends IAlertService {
  alerts: Alert[];
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>(alertStore.alerts);

  useEffect(() => {
    const handleChange = (nextAlerts: Alert[]) => {
      setAlerts(nextAlerts);
    };

    alertStore.onChange(handleChange);

    return () => {
      // There is currently no way to unsubscribe an onChange callback
      // from alertStore. If that changes, wire it up here.
    };
  }, []);

  const api = useMemo(
    () => ({
      onChange: alertStore.onChange.bind(alertStore),
      info: alertStore.info.bind(alertStore),
      success: alertStore.success.bind(alertStore),
      warning: alertStore.warning.bind(alertStore),
      error: alertStore.error.bind(alertStore),
      addAlert: alertStore.addAlert.bind(alertStore),
      clear: alertStore.clear.bind(alertStore),
      clearAll: alertStore.clearAll.bind(alertStore),
    }),
    []
  );

  const value = useMemo<AlertContextValue>(
    () => ({
      alerts,
      ...api,
    }),
    [alerts, api]
  );

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};

export const useAlerts = (): AlertContextValue => {
  const context = useContext(AlertContext);

  if (context == null) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }

  return context;
};
